import axios, { AxiosResponse } from 'axios';
import { ApiResponse, Category, NewsArticle, PaginatedResponse } from '../types';
import { API_URL } from '../config/envConfig';
// Mock verileri yalnızca geliştirme aşamasında kullanılır
// import { categories, newsArticles, getFeaturedNews, getNewsByCategory } from '../utils/mockData';

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Mock API implementation until backend is ready
const mockApi = {
  // Helper to simulate API delay
  delay: (ms: number) => new Promise(resolve => setTimeout(resolve, ms)),
  
  // Create a paginated response from an array
  createPaginatedResponse: <T>(
    items: T[], 
    page: number = 0, 
    size: number = 10
  ): PaginatedResponse<T> => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    const paginatedItems = items.slice(startIndex, endIndex);
    
    return {
      content: paginatedItems,
      totalPages: Math.ceil(items.length / size),
      totalElements: items.length,
      size: size,
      number: page,
      first: page === 0,
      last: endIndex >= items.length,
      empty: paginatedItems.length === 0
    };
  }
};

// Category API calls
export const categoryApi = {
  getAll: async (): Promise<Category[]> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<Category[]>> = await api.get('/categories');
    return response.data.data;
  },
  
  getById: async (id: string): Promise<Category> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<Category>> = await api.get(`/categories/${id}`);
    return response.data.data;
  },
  
  create: async (category: Omit<Category, 'id' | 'createdDate' | 'updatedDate'>): Promise<Category> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<Category>> = await api.post('/categories', category);
    return response.data.data;
  },
  
  update: async (id: string, category: Partial<Category>): Promise<Category> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<Category>> = await api.put(`/categories/${id}`, category);
    return response.data.data;
  },
  
  delete: async (id: string): Promise<void> => {
    // Gerçek API çağrısı
    await api.delete(`/categories/${id}`);
  }
};

// News API calls
export const newsApi = {
  getAll: async (page = 0, size = 30): Promise<PaginatedResponse<NewsArticle>> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<PaginatedResponse<NewsArticle>>> = 
      await api.get(`/news?page=${page}&size=${size}`);
    return response.data.data;
  },
  
  getByCategory: async (categoryId: string, page = 0, size = 30): Promise<PaginatedResponse<NewsArticle>> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<PaginatedResponse<NewsArticle>>> = 
      await api.get(`/news/category/${categoryId}?page=${page}&size=${size}`);
    return response.data.data;
  },
  
  getFeatured: async (): Promise<NewsArticle[]> => {
    // Gerçek API çağrısı - backend'de bu endpoint henüz yoksa, özel bir sorgu yapılabilir
    // const response: AxiosResponse<ApiResponse<NewsArticle[]>> = await api.get('/news/featured');
    // return response.data.data;

    // Veya featured parametresiyle sorgulama yapılabilir
    const response: AxiosResponse<ApiResponse<PaginatedResponse<NewsArticle>>> =
      await api.get('/news?featured=true&size=5');
    return response.data.data.content;
  },

  getLatest: async (size: number = 10): Promise<NewsArticle[]> => {
    // Son eklenen haberleri tarihe göre sıralanmış şekilde getir
    const response: AxiosResponse<ApiResponse<PaginatedResponse<NewsArticle>>> =
      await api.get(`/news?size=${size}&sort=createdDate,desc`);
    return response.data.data.content;
  },
  
  getById: async (id: string, countView: boolean = true): Promise<NewsArticle> => {
    // Gerçek API çağrısı
    const response: AxiosResponse<ApiResponse<NewsArticle>> = await api.get(`/news/${id}?countView=${countView}`);
    return response.data.data;
  },
  
  getBySlug: async (slug: string, countView: boolean = true): Promise<NewsArticle> => {
    try {
      const response: AxiosResponse<ApiResponse<NewsArticle>> = await api.get(`/news/slug/${slug}?countView=${countView}`);
      return response.data.data;
    } catch (error) {
      throw new Error('Article not found');
    }
  },
  
  create: async (article: FormData): Promise<NewsArticle> => {
    // Gerçek API çağrısı - multipart/form-data kullanımı
    const response: AxiosResponse<ApiResponse<NewsArticle>> = await api.post('/news', article, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },
  
  update: async (id: string, article: FormData): Promise<NewsArticle> => {
    // Gerçek API çağrısı - multipart/form-data kullanımı
    const response: AxiosResponse<ApiResponse<NewsArticle>> = await api.put(`/news/${id}`, article, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },
  
  delete: async (id: string): Promise<void> => {
    // Gerçek API çağrısı
    await api.delete(`/news/${id}`);
  }
};

export default api; 